import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData, getMimeTypeAndExtension } from '../../../../utils/fileUpload'

interface SendMessageData {
  conversationId: string
  content: string
  /** Optional image URI (data URI on web, file path on native). */
  imageUri?: string
}

interface SendMessageResponse {
  id: string
}

export const sendMessageRequest = async (
  data: SendMessageData,
): Promise<SendMessageResponse> => {
  // With an image we must use multipart/form-data so the API can stream the
  // file to S3. Without one we keep the lighter JSON payload.
  if (data.imageUri) {
    const formData = new FormData()
    if (data.content) {
      formData.append('content', data.content)
    }
    const { extension } = getMimeTypeAndExtension(data.imageUri)
    appendAvatarToFormData(formData, data.imageUri, 'image', `message.${extension}`)

    const { data: responseData } = await api.post<SendMessageResponse>(
      `/conversations/${data.conversationId}/messages`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return responseData
  }

  const { data: responseData } = await api.post<SendMessageResponse>(
    `/conversations/${data.conversationId}/messages`,
    {
      content: data.content,
    },
  )
  return responseData
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()

  return useMutation<SendMessageResponse, Error, SendMessageData>({
    mutationFn: sendMessageRequest,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [`messages-${variables.conversationId}`] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['conversation', variables.conversationId] }),
      ])
    },
  })
}
